<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { maxLength, minLength, required, requiredIf } from '@vuelidate/validators';
import { useApi } from '@/api';
import { useValidate } from '@/composables/useValidate';
import { useTypedStore } from '@/store';
import type { EmitsToListeners } from '@/types/core';
import type {
  PostDomainsManagementRegistrarAccountsData,
} from '@/types/openapi-ts';
import type { OverrideFields } from '@/types/util';
//#endregion

//#region types
interface Props {
  domainId?: string | null,
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface DomainRegistrarAccountParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

type FormType = OverrideFields<PostDomainsManagementRegistrarAccountsData['body'], {
  id?: string
}>;
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  domainId: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref({});

const form = ref<FormType>({
  name: '',
  auto_ns_management: true,
  domain_registrar_id: null,
  auth_data: {},
});
//#endregion

//#region computed
const domainRegistrarProviders = computed(() => store.getters['misc/domainRegistrarProviders']);

const isEdit = computed(() => {
  return props.domainId;
});

const pPopupTitle = computed(() => {
  return `${t(`domainManagement.popup.${isEdit.value ? 'edit' : 'add'}`)} ${t('domainManagement.popup.domainRegistrarAccount')}`;
});

const domainRegistrar = computed(() => {
  if (form.value.domain_registrar_id && domainRegistrarProviders.value) {
    const [domainRegistrarProvider] = domainRegistrarProviders.value
      .filter((provider) => provider.id === form.value.domain_registrar_id);
    return domainRegistrarProvider;
  }
  return null;
});

const authenticationArrayFields = computed(() => {
  if (form.value.domain_registrar_id) {
    return domainRegistrar.value?.possible_auth_keys || [];
  }
  return [];
});

const activeGroup = computed(() => {
  const isEmptyValues = Object.values(form.value.auth_data!).every((el) => !el);

  return authenticationArrayFields.value.map((child) => {
    if (isEmptyValues) return true;
    return child.some((el) => !!form.value.auth_data![el]);
  });
});

const rules = computed(() => {
  const authDataRules = authenticationArrayFields.value.reduce((acc, authenticationFields, i) => {
    authenticationFields.forEach((authenticationField) => {
      acc[authenticationField] = {
        required: requiredIf(() => form.value.auto_ns_management && !!activeGroup.value[i]),
      };
    });

    return acc;
  }, {} as Record<string, any>);

  return {
    name: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(255),
    },
    domain_registrar_id: {
      required,
    },
    auth_data: authDataRules,
  };
});
//#endregion

//#region methods
async function open() {
  if (isEdit.value) {
    const { data: { payload } } = await api.getDomainRegistrarAccountById(props.domainId!);
    const {
      id,
      name,
      auto_ns_management,
      domain_registrar_id,
      auth_data,
    } = payload;

    form.value = {
      id,
      name,
      auto_ns_management,
      domain_registrar_id,
      auth_data,
    };
  }
  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSave() {
  isLoading.value = true;
  errors.value = {};
  let promise;

  if(isEdit.value) {
    promise = api.updateDomainRegistrarAccount(form.value.id!, form.value);
  } else {
    promise = api.createDomainRegistrarAccount(form.value);
  }

  promise.then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    errors.value = error.data.errors;
  }).finally(() => {
    isLoading.value = false;
  });
}

function changeDomainRegistrarId() {
  form.value.auth_data = {};

  if (domainRegistrar.value?.auto_ns_management_available === false) {
    form.value.auto_ns_management = false;
  }
}
//#endregion

//#region validate
const { checkField, isInvalidField, isInvalidForm } = useValidate({
  rules: rules,
  form: form,
});
//#endregion

defineExpose({
  open,
});
</script>
<template>
  <Popup
    v-model:is-visible="isVisible"
    class="domain-registrar-account-popup"
    :title="pPopupTitle"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="form.name"
          :label="$t(`domainManagement.popup.form.account_name.label`)"
          :placeholder="$t(`domainManagement.popup.form.account_name.placeholder`)"
          autosize
          required
          size="big"
          :is-error="isInvalidField('name')"
          @blur="checkField('name')"
        />
        <UiSelectElement
          v-model="form.domain_registrar_id"
          :label="$t('domainManagement.popup.form.domin_registrar_provider.label')"
          :placeholder="$t('domainManagement.popup.form.domin_registrar_provider.placeholder')"
          :list="domainRegistrarProviders"
          filterable
          required
          size="big"
          :is-error="isInvalidField('domain_registrar_id')"
          @blur="checkField('domain_registrar_id')"
          @change="changeDomainRegistrarId"
        />
        <UiRadioGroup
          v-model="form.auto_ns_management"
          :label="$t('domainManagement.popup.form.auto_dns_management.label')"
          :options="[
            {
              label: $t(`domainManagement.popup.form.auto_dns_management.auto`),
              value: true,
              disabled: domainRegistrar?.auto_ns_management_available === false
            },
            {
              label: $t(`domainManagement.popup.form.auto_dns_management.manual`),
              value: false,
            }
          ]"
        />

        <template v-if="form.auto_ns_management && authenticationArrayFields && authenticationArrayFields.length">
          <UiSeparator />
          <template
            v-for="(authenticationFields, index) in authenticationArrayFields"
            :key="index"
          >
            <UiInput
              v-for="authenticationField in authenticationFields"
              :key="authenticationField"
              v-model="form.auth_data![authenticationField]"
              :label="$t(`domainManagement.popup.form.${authenticationField}.label`)"
              :placeholder="$t(`domainManagement.popup.form.${authenticationField}.placeholder`)"
              :required="activeGroup[index]"
              :disabled="!activeGroup[index]"
              autosize
              size="big"
              :is-error="isInvalidField(`auth_data.${authenticationField}`)"
              @blur="checkField(`auth_data.${authenticationField}`)"
            />
            <UiSeparator v-if="authenticationArrayFields.length - 1 !== index" />
          </template>
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t(`domainManagement.popup.cancel`) }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isInvalidForm || isLoading"
          @click="handleSave"
        >
          {{ $t(`domainManagement.popup.save`) }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
